import type { Invoice } from '../../types';
import type { InvoiceResponseDto } from '../../api/models/InvoiceResponseDto';

export type CreditCardFeeBreakdown = {
  transactionFee: number;
  fee: number;
};

const statusMap: Record<string, Invoice['status']> = {
  DRAFT: 'draft',
  APPROVED: 'approved',
  AWAITING_PAYMENT: 'awaiting-payment',
  PART_PAID: 'part-paid',
  OVERDUE: 'overdue',
  FAILED: 'failed',
  PAID: 'paid',
  REFUNDED: 'refunded',
  WRITTEN_OFF: 'written-off',
  VOID: 'void',
};

const typeMap: Record<string, Invoice['type']> = {
  ANNUAL: 'annual',
  INSTALMENT: 'instalment',
  ADJUSTMENT: 'adjustment',
  RENEWAL: 'renewal',
  MTA: 'mta',
};

const paymentMethodMap: Record<string, Invoice['paymentMethod']> = {
  DIRECT_DEBIT: 'direct-debit',
  CREDIT_CARD: 'credit-card',
  BANK_TRANSFER: 'bank-transfer',
  PREMIUM_FUNDING: 'premium-funding',
  OTHER: 'other',
};

const paymentTypeMap: Record<string, Invoice['paymentType']> = {
  FUNDED: 'funded',
  ANNUAL: 'annual',
};

const insurerMap: Record<string, Invoice['insurer']> = {
  ANDO: 'Ando',
  FMG: 'FMG',
  NZI: 'NZI',
  TOWER: 'Tower',
  VERO: 'Vero',
};

const policyProductMap: Record<string, Invoice['policyProduct']> = {
  HOUSE: 'House',
  CONTENTS: 'Contents',
  VEHICLE: 'Vehicle',
  COMMERCIAL_COMBINED: 'Commercial Combined',
};

const invoiceTypeLabels: Record<Invoice['type'], string> = {
  annual: 'Annual',
  instalment: 'Instalment',
  adjustment: 'Adjustment',
  renewal: 'Renewal',
  mta: 'MTA',
};

export function getInvoiceTypeLabel(type: Invoice['type']): string {
  return invoiceTypeLabels[type] || type;
}

export function getInvoiceTypeRelatedLabel(type: Invoice['type']): string | null {
  if (type === 'adjustment') {
    return 'Policy cancellation';
  }

  return null;
}

export function buildInvoiceDisplayLineItems(
  productSnapshot: Invoice['productSnapshot'],
): Array<{ description: string; amount: number }> {
  const productLineItems = productSnapshot?.products ?? [];
  const lineItems = productLineItems.map((item) => ({
    description: item.product_name,
    amount: Number(item.product_price || 0),
  }));

  const feeBreakdown = productSnapshot?.feeBreakdown ?? [];
  if (feeBreakdown.length > 0) {
    return [
      ...lineItems,
      ...feeBreakdown.map((item) => ({
        description: item.description,
        amount: Number(item.amount || 0),
      })),
    ];
  }

  if (productSnapshot?.adminFee) {
    lineItems.push({
      description: 'Administration Fee',
      amount: Number(productSnapshot.adminFee || 0),
    });
  }

  return lineItems;
}

export function calculateInvoiceSubtotalExGst(
  productSnapshot: Invoice['productSnapshot'],
  policyNetTotal?: number,
): number {
  const lineItems = buildInvoiceDisplayLineItems(productSnapshot);
  if (lineItems.length > 0) {
    return Number(lineItems.reduce((sum, item) => sum + item.amount, 0).toFixed(2));
  }

  return Number(policyNetTotal ?? 0);
}

export function readCreditCardFeesFromProductSnapshot(
  productSnapshot: Invoice['productSnapshot'],
): CreditCardFeeBreakdown | null {
  if (!productSnapshot) {
    return null;
  }

  const transactionFee = Number(productSnapshot.transactionFee ?? 0);
  const fee = Number(productSnapshot.fee ?? 0);

  if (!Number.isFinite(transactionFee) || !Number.isFinite(fee) || (transactionFee <= 0 && fee <= 0)) {
    return null;
  }

  return { transactionFee, fee };
}

export function buildCreditCardPaymentMethodSummary(
  productSnapshot: Invoice['productSnapshot'],
  paymentMethod: Invoice['paymentMethod'],
  invoiceAmount: number,
): {
  transactionFee: number;
  administrationFee: number;
  totalCharged: number;
} | null {
  const normalizedMethod = paymentMethod?.toLowerCase().replace('_', '-');
  if (normalizedMethod !== 'credit-card') {
    return null;
  }

  const creditCardFees = readCreditCardFeesFromProductSnapshot(productSnapshot);
  if (!creditCardFees) {
    return null;
  }

  return {
    transactionFee: creditCardFees.transactionFee,
    administrationFee: creditCardFees.fee,
    totalCharged:
      Math.round((invoiceAmount + creditCardFees.transactionFee + creditCardFees.fee) * 100) / 100,
  };
}

export function transformInvoiceToFrontend(apiData: InvoiceResponseDto | any): Invoice {
  return {
    id: apiData.id,
    invoiceNumber: apiData.invoiceNumber,
    invoiceSequence: apiData.invoiceSequence,
    clientId: apiData.clientId,
    clientName: apiData.clientName,
    policyId: apiData.policyId,
    andoPolicyId: apiData.policyId,
    policyMtaVersion: apiData.policyMtaVersion ?? apiData.policy?.mtaVersion ?? null,
    policyProduct: policyProductMap[apiData.policyProduct] || 'Commercial Combined',
    productSnapshot: apiData.productSnapshot || undefined,
    premiumFundingDetails: apiData.premiumFundingDetails || undefined,
    issueDate: apiData.issueDate,
    dueDate: apiData.dueDate,
    type: typeMap[apiData.type] || 'annual',
    amount: Number(apiData.amount || 0),
    policyNetTotal: Number(apiData.policyNetTotal || 0),
    policyTotalTax: Number(apiData.policyTotalTax || 0),
    paid: Number(apiData.paid || 0),
    outstanding: Number(apiData.outstanding || 0),
    invoicePaid: Boolean(apiData.invoicePaid),
    commissionPaid: Boolean(apiData.commissionPaid),
    insurerPaid: Boolean(apiData.insurerPaid),
    status: statusMap[apiData.status] || 'awaiting-payment',
    paymentMethod: paymentMethodMap[apiData.paymentMethod] || 'bank-transfer',
    paymentType: paymentTypeMap[apiData.paymentType] || 'annual',
    insurer: insurerMap[apiData.insurer] || 'Ando',
    assignedAdviser: apiData.assignedAdviserId || '',
    assignedAdviserName: apiData.assignedAdviserName || undefined,
    company: apiData.company || undefined,
  };
}
